/**
 * @param prop - The property to sort by. It can be a string or number.
 * @returns A comparison function that can be used with the `sort` method of an array.
 * The function compares two objects based on the specified property.
 * It handles different types of values (string, number, boolean)
 * and ensures that the comparison is valid.
 * @example
 * const people = [
 *  { id: 1, name: 'Alice', age: 30 },
 *  { id: 2, name: 'Bob', age: 25 },
 *  { id: 3, name: 'Charlie', age: 35 }
 * ];
 * const sortedByAge = people.sort(sortByProp('age'));
 * const sortedByName = people.sort(sortByProp('name'));
 */
export const sortByProp = <T extends string | number>(prop: T) => {
  return (a: Record<T, any>, b: Record<T, any>) => {
    if (a === b || !(prop in a) || !(prop in b)) {
      return 0;
    }
    const typeA = typeof a?.[prop];
    const typeB = typeof b?.[prop];
    if (typeA !== typeB) {
      return 0;
    }
    switch (typeA) {
      case 'string':
        return a[prop].localeCompare(b[prop]);
      case 'number':
        return a[prop] - b[prop];
      case 'boolean':
        return Number(a[prop]) - Number(b[prop]);
      default:
        return 0;
    }
  };
};
